import { useCallback, useEffect } from 'react';
import { setCookie } from 'nookies';

import { INITIAL_BUSINESS_PUBLIC_ID } from '~/domains/mmp/constants';
import { setHackleUser } from '~/libs/hackle';
import { useBusinessIdStore } from '~/store/global/useBusinessIdStore';
import { getCookieByKey } from '~/utils/cookie';

import { useEffectOnce } from './lifecycle';

declare global {
  interface WindowEventMap {
    changePublicBusinessId: CustomEvent<{ name: string }>;
  }
}

// Cookie에 BusinessId만 없을떄 사용!
export function setBusinessIdToCookie(businessId: string) {
  setCookie(null, 'businessId', businessId, {
    domain: 'cashnote.kr',
    path: '/',
    sameSite: 'strict',
  });
}

export function useStoreBusinessId() {
  const { businessId: localBusinessId, setBusinessId } = useBusinessIdStore();
  const cookieBusinessId = getCookieByKey('businessId');

  const validateBusinessId = useCallback(() => {
    if (cookieBusinessId) {
      setBusinessId?.(cookieBusinessId);
      setHackleUser({
        identifiers: {
          cookieBusinessId,
        },
      });
      return;
    }

    if (localBusinessId && localBusinessId !== 'undefined') {
      setBusinessId?.(localBusinessId);
      setHackleUser({
        identifiers: {
          businessId: localBusinessId,
        },
      });
    }
  }, [cookieBusinessId, localBusinessId, setBusinessId]);

  useEffect(() => {
    const id = (() => {
      // 기본적으로는 앱에서 쿠키로 준 businessId를 사용함.
      if (cookieBusinessId) {
        return `${cookieBusinessId}`;
      }

      // 웹에서 테스트할 때 businessId를 셋팅
      if (localBusinessId) {
        return `${localBusinessId}`;
      }

      return null;
    })();

    if (id) {
      if (id === INITIAL_BUSINESS_PUBLIC_ID) return; // 초기값이 내려오는경우, 딱히 갱신할필요없음
      setBusinessId(id);
      setHackleUser({ id, userId: id });
    }
  }, [cookieBusinessId, localBusinessId, setBusinessId]);

  useEffectOnce(() => {
    validateBusinessId();

    const onChangePublicBusinessId = (e: CustomEvent<{ name: string }>) => {
      if (e?.detail?.name) {
        setBusinessId?.(e.detail.name);
        setBusinessIdToCookie(e.detail.name);
      }
    };

    window.addEventListener('changePublicBusinessId', onChangePublicBusinessId);

    return () => {
      window.removeEventListener(
        'changePublicBusinessId',
        onChangePublicBusinessId,
      );
    };
  });
}
